/**
 * RecommendedPolicies — prototype of the "Recommend" stage of the adaptive policy
 * engine: evidence-backed policy recommendations with a one-click apply flow.
 *
 * All data is mock (see MOCK_RECOMMENDATIONS) — no backend required. To make
 * "Apply" open the real pre-populated Policy Studio editor instead of the built-in
 * modal, see the integration note at `handleApply`.
 */
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Drawer,
  Empty,
  Flex,
  Modal,
  Radio,
  type RadioChangeEvent,
  Result,
  Segmented,
  Spin,
  Statistic,
  Table,
  type TableColumnsType,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

// Types

type Sensitivity = "destructive" | "read" | "write";
type DataSensitivity = "confidential" | "internal" | "pii" | "public";
type RecommendationKind =
  | "agent-least-privilege"
  | "auto-approve-routine"
  | "baseline-restriction"
  | "database-rw"
  | "unused-access";
type RecommendationStatus = "applied" | "dismissed" | "pending";
type RolloutMode = "enforce" | "monitor";

// Minimal, faithful mirror of the P0 policy shape so the proposed `policy`
// objects are exactly what the Policy Studio editor consumes on integration.
type RequestorRule =
  | {
      type: "agentic";
      agent: { type: "any" } | { type: "mcp-client"; clientId: string };
      user: { type: "any" };
    }
  | { type: "any" }
  | { type: "user"; uid: string };
type Filter =
  | { effect: "keep" | "remove"; key: string; pattern: string }
  | { effect: "removeAll" };
type ResourceRule =
  | {
      type: "integration";
      service: string;
      accessType?: string;
      filters?: Record<string, Filter>;
    }
  | { type: "any" };
type ApprovalRule =
  | {
      type: "p0";
      options?: { requireReason?: boolean; requireDuration?: boolean };
    }
  | {
      type: "requestor-profile";
      directory: string;
      profileProperty?: string;
      options?: { requireReason?: boolean };
    }
  | { type: "auto"; integration: string }
  | { type: "deny" }
  | { type: "group" }
  | { type: "persistent"; options?: { requireReason?: boolean } };
type Policy = {
  name?: string;
  requestor: RequestorRule;
  resource: ResourceRule;
  approval: ApprovalRule[];
};

type ToolCallEvidence = {
  tool: string;
  service: string;
  resource: string;
  sensitivity: Sensitivity;
  dataSensitivity: DataSensitivity;
  callCount: number;
  distinctUsers: number;
  lastSeen: number;
};
type ProposedRule = {
  id: string;
  summary: string;
  rationale: string;
  sensitivity: Sensitivity;
  evidenceTools: string[];
  scope?: string;
  policy: Policy;
};
type PolicyRecommendation = {
  id: string;
  kind: RecommendationKind;
  status: RecommendationStatus;
  title: string;
  subtitle: string;
  owner: string;
  signal: string;
  observationWindowDays: number;
  observedSince: number;
  confidence: number;
  summary: string;
  mcpServer?: { name: string; managed: boolean };
  evidence: ToolCallEvidence[];
  proposedRules: ProposedRule[];
  anomalies: string[];
};

// Mock data

const DAY = 24 * 60 * 60 * 1000;
/** Fixed "now" anchor so relative times render identically every run. */
const NOW = 1_783_432_800_000; // 2026-07-07T14:00:00Z

const agentRequestor = (clientId: string): RequestorRule => ({
  type: "agentic",
  agent: { type: "mcp-client", clientId },
  user: { type: "any" },
});

const MOCK_RECOMMENDATIONS: PolicyRecommendation[] = [
  // Agentic least-privilege (mirrors the live demo agents)
  {
    id: "rec_cx_agent",
    kind: "agent-least-privilege",
    status: "pending",
    title: "cxAgent",
    subtitle: "Customer Support Agent",
    owner: "support-eng@acme.com",
    signal: "912 calls · 14d",
    mcpServer: { name: "postgres-mcp (customer-db)", managed: true },
    observationWindowDays: 14,
    observedSince: NOW - 14 * DAY,
    confidence: 0.95,
    summary:
      "Only ever reads the customer table, always scoped to the rows in the requestor's open support ticket — never a successful write. Recommend auto-approving ticket-scoped reads and denying writes for this agent class.",
    evidence: [
      {
        tool: "postgres.select",
        service: "postgres",
        resource: "public.customers (row-level: assigned ticket)",
        sensitivity: "read",
        dataSensitivity: "pii",
        callCount: 907,
        distinctUsers: 23,
        lastSeen: NOW - 40 * 60 * 1000,
      },
      {
        tool: "postgres.update",
        service: "postgres",
        resource: "public.customers.status",
        sensitivity: "write",
        dataSensitivity: "pii",
        callCount: 3,
        distinctUsers: 2,
        lastSeen: NOW - 2 * DAY,
      },
      {
        tool: "postgres.select",
        service: "postgres",
        resource: "public.customers (no matching ticket)",
        sensitivity: "read",
        dataSensitivity: "pii",
        callCount: 2,
        distinctUsers: 2,
        lastSeen: NOW - 3 * DAY,
      },
    ],
    anomalies: [
      "Attempted to set a Trust & Safety–suspended account (Acme) back to active — a write the session had no capability for. Undoing a protection the agent doesn't understand (cf. Replit/SaaStr, PocketOS).",
      "Attempted to read Stark Industries with no matching support ticket — outside the row-level scope, denied.",
    ],
    proposedRules: [
      {
        id: "rec_cx_agent__reads",
        summary: "Auto-approve ticket-scoped reads of the customer table",
        rationale:
          "907 of 912 calls are reads, each satisfied by an open ticket assigned to the requestor. Access is entirely context-scoped, so standing read access with row-level scoping enforces least privilege with no approval friction.",
        sensitivity: "read",
        evidenceTools: ["postgres.select"],
        scope:
          "Ephemeral read-only credential; rows limited to customer_id on the requestor's assigned open ticket; destroyed on read completion.",
        policy: {
          name: "cxAgent — ticket-scoped reads (auto)",
          requestor: agentRequestor("mcp://agents/cx-agent"),
          resource: {
            type: "integration",
            service: "postgres",
            accessType: "read",
            filters: {
              table: {
                effect: "keep",
                key: "table",
                pattern: "public.customers",
              },
            },
          },
          approval: [{ type: "persistent", options: { requireReason: false } }],
        },
      },
      {
        id: "rec_cx_agent__writes",
        summary: "Deny writes for the support agent",
        rationale:
          "No legitimate write was observed; the only attempt tried to reverse a T&S suspension. Support agents should be read-only — deny writes so the capability simply doesn't exist in-session.",
        sensitivity: "write",
        evidenceTools: ["postgres.update"],
        policy: {
          name: "cxAgent — writes (deny)",
          requestor: agentRequestor("mcp://agents/cx-agent"),
          resource: {
            type: "integration",
            service: "postgres",
            accessType: "write",
          },
          approval: [{ type: "deny" }],
        },
      },
    ],
  },
  {
    id: "rec_dev_agent",
    kind: "agent-least-privilege",
    status: "pending",
    title: "devAgent",
    subtitle: "Developer Agent",
    owner: "platform-oncall@acme.com",
    signal: "268 calls · 14d",
    mcpServer: { name: "postgres-mcp (customer-db)", managed: true },
    observationWindowDays: 14,
    observedSince: NOW - 14 * DAY,
    confidence: 0.88,
    summary:
      "Same customer table as cxAgent, but a legitimate write path under a blended human+agent identity. Every write was individually approved. Recommend auto-approve reads, human-in-the-loop writes — the same divergence Policy Studio shows.",
    evidence: [
      {
        tool: "postgres.select",
        service: "postgres",
        resource: "public.customers",
        sensitivity: "read",
        dataSensitivity: "pii",
        callCount: 214,
        distinctUsers: 6,
        lastSeen: NOW - 90 * 60 * 1000,
      },
      {
        tool: "postgres.update",
        service: "postgres",
        resource: "public.customers",
        sensitivity: "write",
        dataSensitivity: "pii",
        callCount: 54,
        distinctUsers: 6,
        lastSeen: NOW - 5 * 60 * 60 * 1000,
      },
    ],
    anomalies: [],
    proposedRules: [
      {
        id: "rec_dev_agent__reads",
        summary: "Auto-approve reads of the customer table",
        rationale:
          "214 reads across 6 on-call engineers; reads are routine and low-risk for this identity. Standing read access removes friction.",
        sensitivity: "read",
        evidenceTools: ["postgres.select"],
        scope: "Ephemeral read-only credential scoped to the session.",
        policy: {
          name: "devAgent — reads (auto)",
          requestor: agentRequestor("mcp://agents/dev-agent"),
          resource: {
            type: "integration",
            service: "postgres",
            accessType: "read",
          },
          approval: [{ type: "persistent" }],
        },
      },
      {
        id: "rec_dev_agent__writes",
        summary: "Require explicit approval for writes (routed to on-call)",
        rationale:
          "All 54 writes went through per-request approval, even though the requestor was on-call. Keep the human in the loop: issue a least-privileged ephemeral credential for the blended Alex+devAgent identity only on approval, with row-level scope carried through to the write.",
        sensitivity: "write",
        evidenceTools: ["postgres.update"],
        scope:
          "On approval: ephemeral least-privileged write credential for the blended human+agent identity; row-level constraint carried through; scoped to the session.",
        policy: {
          name: "devAgent — writes (HITL)",
          requestor: agentRequestor("mcp://agents/dev-agent"),
          resource: {
            type: "integration",
            service: "postgres",
            accessType: "write",
          },
          approval: [{ type: "p0", options: { requireReason: true } }],
        },
      },
    ],
  },
  {
    id: "rec_sales_agent",
    kind: "agent-least-privilege",
    status: "pending",
    title: "salesAgent",
    subtitle: "Sales Agent (Copilot Studio)",
    owner: "sales-ops@acme.com",
    signal: "1,503 calls · 30d",
    mcpServer: { name: "salesforce-mcp", managed: true },
    observationWindowDays: 30,
    observedSince: NOW - 30 * DAY,
    confidence: 0.9,
    summary:
      "Reads opportunities and accounts constantly; a single attempt to bulk-delete opportunities traced to an instruction hidden in a shared document. Recommend auto-approve reads and require manager approval for any write — the policy is the defense, indifferent to intent.",
    evidence: [
      {
        tool: "salesforce.query",
        service: "salesforce",
        resource: "Opportunity, Account",
        sensitivity: "read",
        dataSensitivity: "confidential",
        callCount: 1489,
        distinctUsers: 41,
        lastSeen: NOW - 20 * 60 * 1000,
      },
      {
        tool: "salesforce.delete",
        service: "salesforce",
        resource: "Opportunity",
        sensitivity: "destructive",
        dataSensitivity: "confidential",
        callCount: 14,
        distinctUsers: 1,
        lastSeen: NOW - 4 * DAY,
      },
    ],
    anomalies: [
      "Attempted to delete 14 Salesforce opportunities in one burst — the instruction traced to a shared 'Q1 Board Deck' document (prompt-injection signature). Blocked pending manager approval; denied. Enforcement never had to detect the injection — the write policy stopped it regardless of why the agent asked.",
    ],
    proposedRules: [
      {
        id: "rec_sales_agent__reads",
        summary: "Auto-approve reads of opportunities & accounts",
        rationale:
          "1,489 read queries across 41 sellers over 30 days with a stable pattern. Reads are the agent's core function; standing read access is safe.",
        sensitivity: "read",
        evidenceTools: ["salesforce.query"],
        scope: "Ephemeral read-only credential scoped to the session.",
        policy: {
          name: "salesAgent — CRM reads (auto)",
          requestor: agentRequestor("mcp://agents/sales-agent"),
          resource: {
            type: "integration",
            service: "salesforce",
            accessType: "read",
          },
          approval: [{ type: "persistent" }],
        },
      },
      {
        id: "rec_sales_agent__writes",
        summary: "Require manager approval for any Salesforce write",
        rationale:
          "The only write attempt was a prompt-injected bulk delete. Require manager approval on every write regardless of intent — authorization-layer enforcement is the defense, so the model being tricked never matters.",
        sensitivity: "destructive",
        evidenceTools: ["salesforce.delete"],
        policy: {
          name: "salesAgent — writes (manager approval)",
          requestor: agentRequestor("mcp://agents/sales-agent"),
          resource: {
            type: "integration",
            service: "salesforce",
            accessType: "write",
          },
          approval: [
            {
              type: "requestor-profile",
              directory: "okta",
              profileProperty: "manager",
              options: { requireReason: true },
            },
          ],
        },
      },
    ],
  },
  {
    id: "rec_unmanaged_reporting",
    kind: "agent-least-privilege",
    status: "pending",
    title: "reporting-bot",
    subtitle: "Unknown / Uninventoried",
    owner: "unassigned",
    signal: "631 calls · 9d",
    mcpServer: { name: "postgres-mcp (unmanaged)", managed: false },
    observationWindowDays: 9,
    observedSince: NOW - 9 * DAY,
    confidence: 0.58,
    summary:
      "Newly discovered agent reaching the customer DB through an unmanaged MCP server — a direct pipe with no policy governing it. Nobody hand-authored a rule for this. Recommend bringing it under management with a baseline read-only + approval-on-write policy.",
    evidence: [
      {
        tool: "postgres.select",
        service: "postgres",
        resource: "public.customers, public.invoices",
        sensitivity: "read",
        dataSensitivity: "pii",
        callCount: 604,
        distinctUsers: 3,
        lastSeen: NOW - 3 * 60 * 60 * 1000,
      },
      {
        tool: "postgres.copy",
        service: "postgres",
        resource: "public.customers → CSV export",
        sensitivity: "write",
        dataSensitivity: "pii",
        callCount: 27,
        distinctUsers: 2,
        lastSeen: NOW - 1 * DAY,
      },
    ],
    anomalies: [
      "Reaches production customer data through an unmanaged MCP server with zero policy coverage — inventory flagged it as a direct pipe into your systems.",
      "Bulk-exported customer rows to CSV 27 times — no owner, no approval path, no scoping.",
    ],
    proposedRules: [
      {
        id: "rec_unmanaged_reporting__reads",
        summary: "Baseline: require approval for reads until owned",
        rationale:
          "This agent has no owner and no policy. Until it's brought under management, gate reads behind human approval rather than allowing an ungoverned pipe to PII.",
        sensitivity: "read",
        evidenceTools: ["postgres.select"],
        policy: {
          name: "reporting-bot — reads (HITL, baseline)",
          requestor: agentRequestor("mcp://unmanaged/reporting-bot"),
          resource: {
            type: "integration",
            service: "postgres",
            accessType: "read",
          },
          approval: [{ type: "p0", options: { requireReason: true } }],
        },
      },
      {
        id: "rec_unmanaged_reporting__exports",
        summary: "Deny bulk exports",
        rationale:
          "Bulk CSV export of PII with no scoping or approval is exactly the exfiltration risk an unmanaged pipe creates. Deny it outright pending an owner and an explicit policy.",
        sensitivity: "destructive",
        evidenceTools: ["postgres.copy"],
        policy: {
          name: "reporting-bot — bulk export (deny)",
          requestor: agentRequestor("mcp://unmanaged/reporting-bot"),
          resource: {
            type: "integration",
            service: "postgres",
            accessType: "export",
          },
          approval: [{ type: "deny" }],
        },
      },
    ],
  },

  // Non-agentic wins
  {
    id: "rec_unused_admin",
    kind: "unused-access",
    status: "pending",
    title: "bob.chen@acme.com",
    subtitle: "Standing entitlement · AWS AdministratorAccess (prod)",
    owner: "bob.chen@acme.com",
    signal: "Unused 87d",
    observationWindowDays: 90,
    observedSince: NOW - 90 * DAY,
    confidence: 0.91,
    summary:
      "Bob holds standing AdministratorAccess on the production AWS account, but assessment shows it hasn't been used in 87 days. Recommend revoking the standing grant and converting it to JIT-requestable with approval.",
    evidence: [],
    anomalies: [
      "Standing admin access to production, unused for 87 days — a dormant high-privilege grant is pure attack surface.",
    ],
    proposedRules: [
      {
        id: "rec_unused_admin__jit",
        summary: "Revoke standing access; convert to JIT-requestable",
        rationale:
          "90-day assessment shows zero use. Remove the standing grant and require an approved, time-boxed request the next time it's actually needed — least privilege without blocking real work.",
        sensitivity: "write",
        evidenceTools: [],
        scope:
          "Standing grant revoked; access becomes request-only, time-boxed, and approved per use.",
        policy: {
          name: "AWS admin (bob.chen) — JIT with approval",
          requestor: { type: "user", uid: "bob.chen@acme.com" },
          resource: {
            type: "integration",
            service: "aws",
            accessType: "AdministratorAccess",
          },
          approval: [
            {
              type: "p0",
              options: { requireReason: true, requireDuration: true },
            },
          ],
        },
      },
    ],
  },
  {
    id: "rec_auto_approve_bq",
    kind: "auto-approve-routine",
    status: "pending",
    title: "carol.diaz@acme.com → BigQuery read",
    subtitle: "Recurring JIT request · GCP BigQuery (analytics)",
    owner: "data-eng@acme.com",
    signal: "12/12 approved",
    observationWindowDays: 60,
    observedSince: NOW - 60 * DAY,
    confidence: 0.86,
    summary:
      "Carol's request for read access to the analytics BigQuery dataset (8h) has been approved 12 consecutive times with the same scope. Recommend auto-approving this requester / resource / duration.",
    evidence: [],
    anomalies: [],
    proposedRules: [
      {
        id: "rec_auto_approve_bq__auto",
        summary: "Auto-approve the recurring request at the same scope",
        rationale:
          "12 consecutive manual approvals with identical requester, resource, and duration — the approval step has become a rubber stamp. Auto-approve at exactly that scope and keep the full audit trail.",
        sensitivity: "read",
        evidenceTools: [],
        scope:
          "Same scope as the approved requests: analytics dataset, read-only, 8h ephemeral credential.",
        policy: {
          name: "BigQuery analytics read (carol.diaz) — auto",
          requestor: { type: "user", uid: "carol.diaz@acme.com" },
          resource: {
            type: "integration",
            service: "gcp",
            accessType: "bigquery.read",
            filters: {
              dataset: { effect: "keep", key: "dataset", pattern: "analytics" },
            },
          },
          approval: [{ type: "persistent" }],
        },
      },
    ],
  },
  {
    id: "rec_db_rw_snowflake",
    kind: "database-rw",
    status: "pending",
    title: "Snowflake — ANALYTICS database",
    subtitle: "Database access · read/write split",
    owner: "data-platform@acme.com",
    signal: "R/W split",
    observationWindowDays: 30,
    observedSince: NOW - 30 * DAY,
    confidence: 0.74,
    summary:
      "Access to the ANALYTICS database is granted through a single role that mixes read and write. For database resources we recommend splitting into an auto/JIT read policy and an approval-required write policy.",
    evidence: [],
    anomalies: [],
    proposedRules: [
      {
        id: "rec_db_rw_snowflake__read",
        summary: "Auto-approve reads on the ANALYTICS database",
        rationale:
          "Reads are the dominant, low-risk use of an analytics database. Grant read access as JIT/auto so analysts aren't blocked.",
        sensitivity: "read",
        evidenceTools: [],
        policy: {
          name: "Snowflake ANALYTICS — reads (auto)",
          requestor: { type: "any" },
          resource: {
            type: "integration",
            service: "snowflake",
            accessType: "read",
            filters: {
              database: {
                effect: "keep",
                key: "database",
                pattern: "ANALYTICS",
              },
            },
          },
          approval: [{ type: "persistent" }],
        },
      },
      {
        id: "rec_db_rw_snowflake__write",
        summary: "Require approval for writes on the ANALYTICS database",
        rationale:
          "Writes to a shared analytics database can corrupt downstream reporting. Keep a human in the loop with a reason on every write.",
        sensitivity: "write",
        evidenceTools: [],
        policy: {
          name: "Snowflake ANALYTICS — writes (approval)",
          requestor: { type: "any" },
          resource: {
            type: "integration",
            service: "snowflake",
            accessType: "write",
            filters: {
              database: {
                effect: "keep",
                key: "database",
                pattern: "ANALYTICS",
              },
            },
          },
          approval: [{ type: "p0", options: { requireReason: true } }],
        },
      },
    ],
  },
  {
    id: "rec_baseline_s3",
    kind: "baseline-restriction",
    status: "pending",
    title: "AWS S3 — sensitive buckets",
    subtitle: "Baseline restriction · triggered by AWS connection",
    owner: "security@acme.com",
    signal: "AWS just connected",
    observationWindowDays: 0,
    observedSince: NOW - 1 * DAY,
    confidence: 0.5,
    summary:
      "AWS was just connected. As a starting point, we recommend gating access to sensitive S3 buckets (PII and backups) behind approval rather than leaving them open by default.",
    evidence: [],
    anomalies: [],
    proposedRules: [
      {
        id: "rec_baseline_s3__restrict",
        summary: "Require approval for sensitive S3 bucket access",
        rationale:
          "Sensitive buckets shouldn't be broadly reachable the moment AWS is connected. This is a safe default you can loosen per team once real usage is observed.",
        sensitivity: "read",
        evidenceTools: [],
        scope: "Applies to buckets matching pii-* and *-backups.",
        policy: {
          name: "AWS S3 sensitive buckets — approval required",
          requestor: { type: "any" },
          resource: {
            type: "integration",
            service: "aws",
            accessType: "s3",
            filters: {
              bucket: {
                effect: "keep",
                key: "bucket",
                pattern: "pii-*,*-backups",
              },
            },
          },
          approval: [{ type: "p0", options: { requireReason: true } }],
        },
      },
    ],
  },
  {
    id: "rec_baseline_secrets",
    kind: "baseline-restriction",
    status: "pending",
    title: "AWS Secrets Manager",
    subtitle: "Baseline restriction · triggered by AWS connection",
    owner: "security@acme.com",
    signal: "AWS just connected",
    observationWindowDays: 0,
    observedSince: NOW - 1 * DAY,
    confidence: 0.5,
    summary:
      "Secrets should never be standing access. Recommend gating AWS Secrets Manager reads behind approval by default, JIT and time-boxed.",
    evidence: [],
    anomalies: [],
    proposedRules: [
      {
        id: "rec_baseline_secrets__restrict",
        summary: "Require approval for Secrets Manager access",
        rationale:
          "Standing access to secrets is a common source of blast radius. Make secret reads request-only with approval and a short lease.",
        sensitivity: "read",
        evidenceTools: [],
        scope: "Request-only, time-boxed lease per secret.",
        policy: {
          name: "AWS Secrets Manager — approval required",
          requestor: { type: "any" },
          resource: {
            type: "integration",
            service: "aws",
            accessType: "secretsmanager",
          },
          approval: [
            {
              type: "p0",
              options: { requireReason: true, requireDuration: true },
            },
          ],
        },
      },
    ],
  },
];

// Display helpers

const SENSITIVITY_META: Record<Sensitivity, { label: string; color: string }> =
  {
    read: { label: "Read", color: "green" },
    write: { label: "Write", color: "gold" },
    destructive: { label: "Destructive", color: "red" },
  };
const DATA_META: Record<DataSensitivity, { label: string; color: string }> = {
  public: { label: "Public", color: "default" },
  internal: { label: "Internal", color: "blue" },
  confidential: { label: "Confidential", color: "gold" },
  pii: { label: "PII", color: "volcano" },
};
const KIND_META: Record<RecommendationKind, { label: string; color: string }> =
  {
    "agent-least-privilege": {
      label: "Agent least-privilege",
      color: "geekblue",
    },
    "unused-access": { label: "Unused access", color: "gold" },
    "auto-approve-routine": { label: "Auto-approve routine", color: "green" },
    "database-rw": { label: "Database R/W", color: "cyan" },
    "baseline-restriction": { label: "Baseline restriction", color: "purple" },
  };

const SensitivityTag = ({ sensitivity }: { sensitivity: Sensitivity }) => (
  <Tag color={SENSITIVITY_META[sensitivity].color}>
    {SENSITIVITY_META[sensitivity].label}
  </Tag>
);
const DataSensitivityTag = ({ value }: { value: DataSensitivity }) => (
  <Tooltip title="Inferred from the integration & resource the calls touched">
    <Tag color={DATA_META[value].color}>{DATA_META[value].label}</Tag>
  </Tooltip>
);
const KindTag = ({ kind }: { kind: RecommendationKind }) => (
  <Tag color={KIND_META[kind].color}>{KIND_META[kind].label}</Tag>
);
const ConfidenceTag = ({ confidence }: { confidence: number }) => {
  const pct = Math.round(confidence * 100);
  const color =
    confidence >= 0.85 ? "green" : confidence >= 0.65 ? "gold" : "orange";
  const label =
    confidence >= 0.85 ? "High" : confidence >= 0.65 ? "Medium" : "Low";
  return (
    <Tooltip title="Driven by call volume and behavioral consistency">
      <Tag color={color}>{`${label} · ${pct}%`}</Tag>
    </Tooltip>
  );
};

const resourceSummary = (resource: ResourceRule): string => {
  if (resource.type === "any") return "Any resource";
  const access = resource.accessType ? ` · ${resource.accessType}` : "";
  const filters = resource.filters
    ? " · " +
      Object.values(resource.filters)
        .map((f) => ("pattern" in f ? f.pattern : f.effect))
        .join(", ")
    : "";
  return `${resource.service}${access}${filters}`;
};
const approvalSummary = (approval: ApprovalRule[]): string =>
  approval
    .map((rule) => {
      switch (rule.type) {
        case "persistent":
          return "Standing access (auto-approved)";
        case "p0":
          return rule.options?.requireReason
            ? "Human approval — reason required"
            : "Human approval";
        case "deny":
          return "Denied";
        case "auto":
          return `Auto-approved via ${rule.integration}`;
        case "group":
          return "Group approval";
        case "requestor-profile":
          return "Manager approval";
        default:
          return "Approval required";
      }
    })
    .join(" · ");
const approvalColor = (approval: ApprovalRule[]): string => {
  if (approval.some((r) => r.type === "deny")) return "red";
  if (approval.some((r) => r.type === "persistent" || r.type === "auto"))
    return "green";
  return "gold";
};
const relativeTime = (epochMs: number): string => {
  const diff = Math.max(0, NOW - epochMs);
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

// Apply modal

const RuleSelectRow = ({
  rule,
  checked,
  onToggle,
}: {
  rule: ProposedRule;
  checked: boolean;
  onToggle: (id: string) => void;
}) => {
  const handleToggle = useCallback(
    () => onToggle(rule.id),
    [onToggle, rule.id]
  );
  return (
    <Flex gap={12} align="start">
      <Checkbox checked={checked} onChange={handleToggle} />
      <Flex vertical gap={4} flex={1}>
        <Flex align="center" justify="space-between" gap={8}>
          <Typography.Text strong>{rule.summary}</Typography.Text>
          <SensitivityTag sensitivity={rule.sensitivity} />
        </Flex>
        <Flex gap={8} wrap="wrap">
          <Tag>{resourceSummary(rule.policy.resource)}</Tag>
          <Tag color={approvalColor(rule.policy.approval)}>
            {approvalSummary(rule.policy.approval)}
          </Tag>
        </Flex>
        {rule.scope && (
          <Flex gap={6} align="baseline">
            <SafetyCertificateOutlined />
            <Typography.Text type="secondary" italic>
              {rule.scope}
            </Typography.Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const ApplyModal = ({
  recommendation,
  onConfirm,
  onClose,
}: {
  recommendation: PolicyRecommendation | null;
  onConfirm: (id: string) => void;
  onClose: () => void;
}) => {
  const [step, setStep] = useState<"applying" | "done" | "review">("review");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<RolloutMode>("enforce");

  useEffect(() => {
    if (recommendation) {
      setStep("review");
      setSelected(new Set(recommendation.proposedRules.map((r) => r.id)));
      setMode("enforce");
    }
  }, [recommendation]);

  const toggleRule = useCallback((id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectedRules = useMemo(
    () =>
      recommendation
        ? recommendation.proposedRules.filter((r) => selected.has(r.id))
        : [],
    [recommendation, selected]
  );

  const handleApply = useCallback(async () => {
    if (!recommendation) return;
    setStep("applying");
    // Brief beat so the apply feels like it did work before success.
    await new Promise((r) => setTimeout(r, 700));
    onConfirm(recommendation.id);
    setStep("done");
  }, [recommendation, onConfirm]);

  const handleModeChange = useCallback(
    (e: RadioChangeEvent) => setMode(e.target.value),
    []
  );

  if (!recommendation) return null;

  const footer =
    step === "review" ? (
      <Flex justify="flex-end" gap={8}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          disabled={selected.size === 0}
          onClick={handleApply}
        >
          {`Apply ${selected.size} ${selected.size === 1 ? "policy" : "policies"}`}
        </Button>
      </Flex>
    ) : step === "done" ? (
      <Flex justify="flex-end">
        <Button type="primary" onClick={onClose}>
          Done
        </Button>
      </Flex>
    ) : null;

  return (
    <Modal
      title={
        step === "done"
          ? undefined
          : `Apply recommendation — ${recommendation.title}`
      }
      width={620}
      open
      onCancel={onClose}
      footer={footer}
      maskClosable={step !== "applying"}
      closable={step !== "applying"}
      destroyOnHidden
    >
      {step === "review" && (
        <Flex vertical gap={16}>
          <Typography.Text type="secondary">
            These policies will be created for{" "}
            <Typography.Text strong>{recommendation.title}</Typography.Text>.
            Uncheck any you don&apos;t want to apply.
          </Typography.Text>
          <Flex vertical gap={16}>
            {recommendation.proposedRules.map((rule) => (
              <RuleSelectRow
                key={rule.id}
                rule={rule}
                checked={selected.has(rule.id)}
                onToggle={toggleRule}
              />
            ))}
          </Flex>
          <Divider style={{ margin: 0 }} />
          <Flex vertical gap={8}>
            <Typography.Text strong>Rollout</Typography.Text>
            <Radio.Group value={mode} onChange={handleModeChange}>
              <Flex vertical gap={8}>
                <Radio value="enforce">
                  <Typography.Text>Enable immediately</Typography.Text>
                  <br />
                  <Typography.Text type="secondary">
                    Policies take effect on the next request.
                  </Typography.Text>
                </Radio>
                <Radio value="monitor">
                  <Typography.Text>Shadow first (monitor only)</Typography.Text>
                  <br />
                  <Typography.Text type="secondary">
                    Log what would happen for 7 days without enforcing — verify
                    before it blocks anything.
                  </Typography.Text>
                </Radio>
              </Flex>
            </Radio.Group>
          </Flex>
        </Flex>
      )}

      {step === "applying" && (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={16}
          style={{ padding: "40px 0" }}
        >
          <Spin size="large" />
          <Typography.Text type="secondary">Applying policies…</Typography.Text>
        </Flex>
      )}

      {step === "done" && (
        <Result
          status="success"
          title={`${selectedRules.length} ${
            selectedRules.length === 1 ? "policy" : "policies"
          } applied`}
          subTitle={
            mode === "enforce"
              ? `${recommendation.title} is now governed and enforced.`
              : `${recommendation.title} is in shadow mode — monitoring for 7 days before enforcing.`
          }
        >
          <Flex vertical gap={8}>
            {mode === "monitor" && (
              <Alert
                type="info"
                showIcon
                message="Shadow mode: these policies are logging matches but not blocking yet."
              />
            )}
            {selectedRules.map((rule) => (
              <Flex
                key={rule.id}
                align="center"
                justify="space-between"
                gap={8}
              >
                <Flex align="center" gap={8}>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  <Typography.Text>{rule.policy.name}</Typography.Text>
                </Flex>
                <Tag color={approvalColor(rule.policy.approval)}>
                  {approvalSummary(rule.policy.approval)}
                </Tag>
              </Flex>
            ))}
          </Flex>
        </Result>
      )}
    </Modal>
  );
};

// Detail drawer

const evidenceRowKey = (e: ToolCallEvidence) => `${e.tool}:${e.resource}`;

const evidenceColumns: TableColumnsType<ToolCallEvidence> = [
  {
    title: "Tool",
    key: "tool",
    render: (_, e) => (
      <Flex vertical>
        <Typography.Text strong>{e.tool}</Typography.Text>
        <Typography.Text type="secondary">{e.resource}</Typography.Text>
      </Flex>
    ),
  },
  {
    title: "Action",
    key: "sensitivity",
    render: (_, e) => <SensitivityTag sensitivity={e.sensitivity} />,
  },
  {
    title: "Data",
    key: "data",
    render: (_, e) => <DataSensitivityTag value={e.dataSensitivity} />,
  },
  {
    title: "Calls",
    key: "calls",
    align: "right",
    render: (_, e) => e.callCount.toLocaleString(),
  },
  {
    title: "Users",
    key: "users",
    align: "right",
    render: (_, e) => e.distinctUsers,
  },
  {
    title: "Last seen",
    key: "lastSeen",
    align: "right",
    render: (_, e) => (
      <Typography.Text type="secondary">
        {relativeTime(e.lastSeen)}
      </Typography.Text>
    ),
  },
];

const ProposedRuleCard = ({ rule }: { rule: ProposedRule }) => (
  <Card size="small">
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between" gap={8}>
        <Typography.Text strong>{rule.summary}</Typography.Text>
        <SensitivityTag sensitivity={rule.sensitivity} />
      </Flex>
      <Typography.Text type="secondary">{rule.rationale}</Typography.Text>
      <Flex gap={8} wrap="wrap">
        <Tag>{resourceSummary(rule.policy.resource)}</Tag>
        <Tag color={approvalColor(rule.policy.approval)}>
          {approvalSummary(rule.policy.approval)}
        </Tag>
      </Flex>
      {rule.scope && (
        <Flex gap={6} align="baseline">
          <SafetyCertificateOutlined />
          <Typography.Text type="secondary" italic>
            {rule.scope}
          </Typography.Text>
        </Flex>
      )}
    </Flex>
  </Card>
);

const DrawerFooter = ({
  recommendation,
  onApply,
  onDismiss,
}: {
  recommendation: PolicyRecommendation;
  onApply: (rec: PolicyRecommendation) => void;
  onDismiss: (id: string) => void;
}) => {
  const handleDismiss = useCallback(
    () => onDismiss(recommendation.id),
    [onDismiss, recommendation.id]
  );
  const handleApply = useCallback(
    () => onApply(recommendation),
    [onApply, recommendation]
  );
  const count = recommendation.proposedRules.length;
  return (
    <Flex justify="flex-end" gap={8}>
      <Button icon={<CloseOutlined />} onClick={handleDismiss}>
        Dismiss
      </Button>
      <Button type="primary" icon={<CheckOutlined />} onClick={handleApply}>
        {`Apply ${count} ${count === 1 ? "policy" : "policies"}`}
      </Button>
    </Flex>
  );
};

const RecommendationDrawer = ({
  recommendation,
  onClose,
  onApply,
  onDismiss,
}: {
  recommendation: PolicyRecommendation | null;
  onClose: () => void;
  onApply: (rec: PolicyRecommendation) => void;
  onDismiss: (id: string) => void;
}) => {
  if (!recommendation) return null;
  const totalCalls = recommendation.evidence.reduce(
    (s, e) => s + e.callCount,
    0
  );
  const isPending = recommendation.status === "pending";

  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {recommendation.title}
          </Typography.Text>
          {recommendation.status === "applied" && (
            <Tag color="green">Applied</Tag>
          )}
          {recommendation.status === "dismissed" && <Tag>Dismissed</Tag>}
        </Flex>
      }
      width={640}
      open
      onClose={onClose}
      destroyOnHidden
      footer={
        isPending ? (
          <DrawerFooter
            recommendation={recommendation}
            onApply={onApply}
            onDismiss={onDismiss}
          />
        ) : null
      }
    >
      <Flex vertical gap={20}>
        <Flex vertical gap={4}>
          <Flex align="center" gap={8} wrap="wrap">
            <KindTag kind={recommendation.kind} />
            <Tag>{recommendation.subtitle}</Tag>
            <ConfidenceTag confidence={recommendation.confidence} />
            <Tooltip title="Owner the approval loop routes to">
              <Tag>{`owner: ${recommendation.owner}`}</Tag>
            </Tooltip>
            {recommendation.mcpServer && (
              <Tooltip
                title={
                  recommendation.mcpServer.managed
                    ? "Reached through a managed MCP server"
                    : "Unmanaged MCP server — a direct pipe into your systems with no policy governing it"
                }
              >
                <Tag
                  color={recommendation.mcpServer.managed ? "default" : "red"}
                >
                  {recommendation.mcpServer.managed
                    ? recommendation.mcpServer.name
                    : `⚠ ${recommendation.mcpServer.name}`}
                </Tag>
              </Tooltip>
            )}
          </Flex>
          <Typography.Paragraph type="secondary">
            {recommendation.summary}
          </Typography.Paragraph>
        </Flex>

        <Flex gap={24} wrap="wrap">
          {recommendation.evidence.length > 0 && (
            <>
              <Statistic
                title={`Observed (${recommendation.observationWindowDays}d)`}
                value={totalCalls}
              />
              <Statistic
                title="Tool call classes"
                value={recommendation.evidence.length}
              />
            </>
          )}
          <Statistic
            title="Proposed policies"
            value={recommendation.proposedRules.length}
          />
        </Flex>

        {recommendation.anomalies.length > 0 && (
          <Flex vertical gap={8}>
            <Typography.Text strong>Anomalies</Typography.Text>
            {recommendation.anomalies.map((a, i) => (
              <Alert
                key={i}
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message={a}
              />
            ))}
          </Flex>
        )}

        {recommendation.evidence.length > 0 && (
          <Flex vertical gap={8}>
            <Typography.Text strong>What we observed</Typography.Text>
            <Table
              columns={evidenceColumns}
              dataSource={recommendation.evidence}
              rowKey={evidenceRowKey}
              pagination={false}
              size="small"
            />
          </Flex>
        )}

        <Flex vertical gap={8}>
          <Typography.Text strong>Recommended policy</Typography.Text>
          <Flex vertical gap={8}>
            {recommendation.proposedRules.map((rule) => (
              <ProposedRuleCard key={rule.id} rule={rule} />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Drawer>
  );
};

// Main component

type StatusFilter = RecommendationStatus | "all";

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const RowActions = ({
  rec,
  onApply,
  onDismiss,
}: {
  rec: PolicyRecommendation;
  onApply: (rec: PolicyRecommendation) => void;
  onDismiss: (id: string) => void;
}) => {
  const handleDismiss = useCallback(
    () => onDismiss(rec.id),
    [onDismiss, rec.id]
  );
  const handleApply = useCallback(() => onApply(rec), [onApply, rec]);
  return (
    <div onClick={stopPropagation}>
      <Flex gap={8} justify="flex-end">
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          onClick={handleApply}
        >
          Apply
        </Button>
      </Flex>
    </div>
  );
};

export default function RecommendedPolicies() {
  const [recommendations, setRecommendations] =
    useState<PolicyRecommendation[]>(MOCK_RECOMMENDATIONS);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [preview, setPreview] = useState<PolicyRecommendation | null>(null);
  const [applyTarget, setApplyTarget] = useState<PolicyRecommendation | null>(
    null
  );

  const setStatus = useCallback(
    (id: string, status: RecommendationStatus) =>
      setRecommendations((cur) =>
        cur.map((r) => (r.id === id ? { ...r, status } : r))
      ),
    []
  );

  const pendingCount = recommendations.filter(
    (r) => r.status === "pending"
  ).length;
  const visible =
    statusFilter === "all"
      ? recommendations
      : recommendations.filter((r) => r.status === statusFilter);

  // INTEGRATION NOTE (P0 app): to open the real pre-populated Policy Studio editor
  // instead of this modal, replace the two lines below with a router navigation,
  // e.g.  navigate("new", { state: { prefillPolicy: rec.proposedRules[0].policy } })
  const handleApply = useCallback((rec: PolicyRecommendation) => {
    setPreview(null);
    setApplyTarget(rec);
  }, []);

  const handleConfirmApply = useCallback(
    (id: string) => setStatus(id, "applied"),
    [setStatus]
  );

  const handleDismiss = useCallback(
    (id: string) => {
      setStatus(id, "dismissed");
      setPreview(null);
      message.info("Recommendation dismissed.");
    },
    [setStatus]
  );

  const handleStatusFilterChange = useCallback(
    (value: StatusFilter) => setStatusFilter(value),
    []
  );

  const handleRow = useCallback(
    (rec: PolicyRecommendation) => ({ onClick: () => setPreview(rec) }),
    []
  );

  const handlePreviewClose = useCallback(() => setPreview(null), []);
  const handleApplyModalClose = useCallback(() => setApplyTarget(null), []);

  const columns: TableColumnsType<PolicyRecommendation> = [
    {
      title: "Subject",
      key: "subject",
      render: (_, rec) => (
        <Flex vertical gap={2}>
          <Flex align="center" gap={6}>
            <Typography.Text strong>{rec.title}</Typography.Text>
            {rec.anomalies.length > 0 && (
              <Tooltip
                title={`${rec.anomalies.length} anomal${rec.anomalies.length === 1 ? "y" : "ies"} flagged`}
              >
                <WarningOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            )}
          </Flex>
          <Typography.Text type="secondary">{rec.subtitle}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: "Type",
      key: "kind",
      width: 190,
      render: (_, rec) => <KindTag kind={rec.kind} />,
    },
    {
      title: "Signal",
      key: "signal",
      width: 150,
      render: (_, rec) => (
        <Typography.Text type="secondary">{rec.signal}</Typography.Text>
      ),
    },
    {
      title: "Policies",
      key: "policies",
      width: 100,
      render: (_, rec) => (
        <Typography.Text type="secondary">{`${rec.proposedRules.length} proposed`}</Typography.Text>
      ),
    },
    {
      title: "Confidence",
      key: "confidence",
      width: 130,
      render: (_, rec) => <ConfidenceTag confidence={rec.confidence} />,
    },
    {
      title: "",
      key: "actions",
      width: 170,
      align: "right",
      render: (_, rec) => {
        if (rec.status === "applied") return <Tag color="green">Applied</Tag>;
        if (rec.status === "dismissed") return <Tag>Dismissed</Tag>;
        return (
          <RowActions
            rec={rec}
            onApply={handleApply}
            onDismiss={handleDismiss}
          />
        );
      },
    },
  ];

  return (
    <div className="recommended-policies">
      <style>{`.recommended-policies .ant-table-tbody > tr { cursor: pointer; }`}</style>
      <Flex vertical gap={16}>
        <Flex align="center" justify="space-between" gap={8} wrap="wrap">
          <Typography.Text type="secondary">{`${pendingCount} pending review`}</Typography.Text>
          <Segmented
            options={[
              { label: "Pending", value: "pending" },
              { label: "Applied", value: "applied" },
              { label: "Dismissed", value: "dismissed" },
              { label: "All", value: "all" },
            ]}
            value={statusFilter}
            onChange={handleStatusFilterChange}
          />
        </Flex>

        {visible.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No recommendations in this view."
          />
        ) : (
          <Table
            columns={columns}
            dataSource={visible}
            rowKey="id"
            pagination={false}
            onRow={handleRow}
          />
        )}
      </Flex>

      <RecommendationDrawer
        recommendation={preview}
        onClose={handlePreviewClose}
        onApply={handleApply}
        onDismiss={handleDismiss}
      />
      <ApplyModal
        recommendation={applyTarget}
        onConfirm={handleConfirmApply}
        onClose={handleApplyModalClose}
      />
    </div>
  );
}
